import {
  AnimatedThemeIcon,
  AnimatedNextyLogo,
  NextyDesignSystemLogo,
  NextyArthurLogo,
  NextyCloudLogo,
} from "@one-nexty/icons";
import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarActions,
  cn,
} from "@one-nexty/components-web";

import { useTheme } from "./lib/use-theme";
import { componentCount, tokenCount } from "./lib/tokens";
import {
  ColorsSection,
  ElevationSection,
  SpacingSection,
  TypographySection,
} from "./sections/foundations";
import { MotionSection } from "./sections/motion";
import { BrandSection } from "./sections/brand";
import { BadgeSection, ButtonSection } from "./sections/actions";
import { IconsSection } from "./sections/icons";
import { FormsSection } from "./sections/forms";
import { FeedbackSection } from "./sections/feedback";
import { DataSection } from "./sections/data";
import { LayoutSection } from "./sections/layout";
import { LayoutingSection } from "./sections/layouting";
import { BrowserElementsSection } from "./sections/browser-elements";
import { NavigationSection, OverlaysSection } from "./sections/navigation";
import { NavbarsSection } from "./sections/navbars";
import { GettingStartedSection } from "./sections/getting-started";
import { AiGuideSection } from "./sections/ai-guide";
import { ComponentSurfacesSection } from "./sections/surfaces";
import { CloudSection } from "./sections/cloud";
import { EditorsSection } from "./sections/editors";

const NAV = [
  {
    group: "Fondations",
    items: [
      { id: "brand-logos", label: "Marque" },
      { id: "couleurs", label: "Couleurs" },
      { id: "typographie", label: "Typographie" },
      { id: "espacements", label: "Espacements" },
      { id: "elevation", label: "Rayons & élévations" },
      { id: "surfaces-composants", label: "Surfaces" },
      { id: "mouvement", label: "Mouvement" },
      { id: "browser-elements", label: "Éléments natifs" },
    ],
  },
  {
    group: "Composants",
    items: [
      { id: "icons", label: "Icônes" },
      { id: "layouting-sota", label: "Layout" },
      { id: "button", label: "Button" },
      { id: "badge", label: "Badge, Kbd & Code" },
      { id: "formulaires", label: "Formulaires" },
      { id: "feedback", label: "Feedback" },
      { id: "donnees", label: "Données" },
      { id: "structure", label: "Structure" },
      { id: "navigation", label: "Navigation" },
      { id: "navbars", label: "Navbars" },
      { id: "superpositions", label: "Superpositions" },
      { id: "cloud-console", label: "Console Cloud" },
      { id: "editeurs", label: "Éditeurs & Code" },
    ],
  },
  {
    group: "Guide",
    items: [
      { id: "demarrer", label: "Installation" },
      { id: "specification-ia", label: "llms.txt" },
    ],
  },
] as const;

const SECTION_IDS = NAV.flatMap((group) => group.items.map((item) => item.id));

/** Met en surbrillance l'entrée de sommaire de la section en cours de lecture. */
function useActiveSection() {
  const [active, setActive] = useState<string>(SECTION_IDS[0] ?? "");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      // La bande couvre le haut du viewport : la section « active » est celle
      // qu'on lit, pas celle qui entre par le bas.
      { rootMargin: "-72px 0px -72% 0px" },
    );

    for (const id of SECTION_IDS) {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, []);

  return active;
}

export function App() {
  const { theme, toggle } = useTheme();
  const active = useActiveSection();

  return (
    <div className="min-h-screen bg-surface">
      {/* ------------------------------------------------------------------- Navbar */}
      <Navbar variant="default" sticky className="z-30">
        <NavbarBrand>
          <a
            href="#top"
            className="flex items-center gap-2.5 rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
          >
            <NextyDesignSystemLogo size={22} />
            <Badge tone="neutral" className="hidden sm:inline-flex text-[10px] px-1.5 py-0.5">
              v0.4.0
            </Badge>
          </a>
        </NavbarBrand>

        {/* Barre des 3 logos produits : nexty design, nexty arthur, nexty cloud */}
        <NavbarContent align="center" className="hidden md:flex items-center gap-4 bg-surface-sunken/60 px-4 py-1.5 rounded-full border border-border/60">
          <a
            href="#brand-logos"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            title="nexty design"
          >
            <NextyDesignSystemLogo size={18} />
          </a>
          <span className="h-4 w-px bg-border/80" />
          <a
            href="#brand-logos"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            title="nexty arthur"
          >
            <NextyArthurLogo size={18} />
          </a>
          <span className="h-4 w-px bg-border/80" />
          <a
            href="#brand-logos"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            title="nexty cloud"
          >
            <NextyCloudLogo size={18} />
          </a>
        </NavbarContent>

        <NavbarActions>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={toggle}
            aria-label={theme === "dark" ? "Passer en thème clair" : "Passer en thème sombre"}
          >
            <AnimatedThemeIcon theme={theme} size={20} />
          </Button>
        </NavbarActions>
      </Navbar>

      <div className="mx-auto flex max-w-7xl gap-12 px-6">
        {/* ------------------------------------------------------------ Sommaire */}
        <nav
          aria-label="Sommaire"
          className="sticky top-16 hidden h-[calc(100vh-4rem)] w-56 shrink-0 overflow-y-auto py-10 lg:block"
        >
          {NAV.map((group) => (
            <div key={group.group} className="mb-6">
              <p className="mb-1.5 px-3 text-[11px] font-semibold tracking-wide text-fg-muted uppercase">
                {group.group}
              </p>
              <ul>
                {group.items.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      aria-current={active === item.id ? "true" : undefined}
                      className={cn(
                        // `tint` : le sommaire est une longue liste que le
                        // curseur traverse vite. Le survol y est un fondu
                        // d'opacité, pas un repeint de fond par entrée.
                        "block rounded-md px-3 py-1.5 text-sm transition-colors",
                        "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                        active === item.id
                          ? "bg-accent-subtle font-medium text-accent-fg"
                          : "tint text-fg-muted hover:text-fg",
                      )}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* -------------------------------------------------------------- Contenu */}
        <main id="top" className="min-w-0 flex-1 pb-24">
          <Hero />
          <BrandSection />
          <ColorsSection />
          <TypographySection />
          <SpacingSection />
          <ElevationSection />
          <ComponentSurfacesSection />
          <MotionSection />
          <BrowserElementsSection />
          <IconsSection />
          <LayoutingSection />
          <ButtonSection />
          <BadgeSection />
          <FormsSection />
          <FeedbackSection />
          <DataSection />
          <LayoutSection />
          <NavigationSection />
          <NavbarsSection />
          <OverlaysSection />
          <CloudSection />
          <EditorsSection />
          <GettingStartedSection />
          <AiGuideSection />
        </main>
      </div>

      <footer className="border-t border-border py-8 bg-surface-raised/40">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 text-sm text-fg-muted">
          <div className="flex items-center gap-3">
            <AnimatedNextyLogo autoAnimate size={28} />
            <span className="font-medium text-fg">© {new Date().getFullYear()} One Nexty</span>
          </div>
          <span className="font-mono text-xs text-fg-muted">@one-nexty/design-system</span>
        </div>
      </footer>
    </div>
  );
}

function Hero() {
  return (
    <div className="py-16">
      <div className="inline-flex items-center gap-2 rounded-full border border-border bg-surface-raised px-3 py-1 text-xs text-fg-muted">
        <span className="size-1.5 rounded-full bg-success" />
        Tailwind v4 · React 19 · OKLCH
      </div>

      <h1 className="mt-6 max-w-3xl text-4xl font-semibold tracking-tight text-balance text-fg sm:text-5xl">
        Une seule source de vérité,{" "}
        <span className="text-accent">toutes les plateformes.</span>
      </h1>

      <p className="mt-5 max-w-2xl text-lg text-fg-muted">
        {tokenCount} tokens W3C DTCG, {componentCount} composants React, un thème Tailwind généré.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <Button size="lg" onClick={() => document.getElementById("demarrer")?.scrollIntoView()}>
          Installer
        </Button>
        <Button
          size="lg"
          variant="outline"
          onClick={() => document.getElementById("couleurs")?.scrollIntoView()}
        >
          Fondations
        </Button>
      </div>
    </div>
  );
}
